import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTourProgress } from "./hooks/useTourProgress";
import { getStepById, tourStepsRegistry } from "./steps/tourStepsRegistry";
import type { TourStep, TourStepOption, TourPreferences, TourTooltipMode } from "./types";

interface TourGuideContextValue {
  /** Is the tooltip currently visible? */
  isOpen: boolean;
  /** The step currently being shown */
  currentStep: TourStep | null;
  /** Engine status */
  isReady: boolean;
  /** Open the tour at the user's saved step (or welcome if none) */
  open: () => void;
  /** Close the tooltip without changing progress */
  close: () => void;
  /** Toggle visibility */
  toggle: () => void;
  /** Move to the next step in the linear journey */
  next: () => void;
  /** Move to the previous step */
  back: () => void;
  /** Skip the current step */
  skip: () => void;
  /** Pick one of the step's options */
  selectOption: (option: TourStepOption) => void;
  /** Reset the entire tour (used by Settings) */
  reset: () => void;
  /** Has user finished the tour? */
  isCompleted: boolean;
  /** Linear progress 0..1 */
  progressRatio: number;
  /** User preferences (language, highlights, tooltip mode) */
  preferences: TourPreferences;
  /** Update one or more preference fields (persisted) */
  setPreferences: (patch: Partial<TourPreferences>) => void;
}

const TourGuideContext = createContext<TourGuideContextValue | undefined>(undefined);

export const useTourGuide = () => {
  const ctx = useContext(TourGuideContext);
  if (!ctx) {
    // Safe fallback when used outside provider (e.g. in Account Center)
    return {
      isOpen: false,
      currentStep: null,
      isReady: false,
      open: () => {},
      close: () => {},
      toggle: () => {},
      next: () => {},
      back: () => {},
      skip: () => {},
      selectOption: () => {},
      reset: () => {},
      isCompleted: false,
      progressRatio: 0,
      preferences: { language: "en", show_highlights: true, tooltip_mode: "anchored" } as TourPreferences,
      setPreferences: () => {},
    } as TourGuideContextValue;
  }
  return ctx;
};

export const TourGuideProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { progress, update, logEvent, isLoading } = useTourProgress();
  const [isOpen, setIsOpen] = useState(false);

  const currentStepId = progress?.current_step_id ?? "welcome";
  const currentStep = useMemo(() => getStepById(currentStepId) ?? tourStepsRegistry[0], [currentStepId]);
  const isCompleted = progress?.tour_status === "completed";

  const progressRatio = useMemo(() => {
    const idx = tourStepsRegistry.findIndex((s) => s.id === currentStepId);
    if (idx < 0) return 0;
    return (idx + 1) / tourStepsRegistry.length;
  }, [currentStepId]);

  // Auto-open on very first login (Phase 1: only triggers if `auto_opened_first_time` is false)
  useEffect(() => {
    if (isLoading || !progress) return;
    if (progress.auto_opened_first_time) return;
    if (progress.tour_status === "completed") return;

    setIsOpen(true);
    update({
      auto_opened_first_time: true,
      tour_status: "in_progress",
    }).catch(() => {});
    logEvent("welcome", "viewed", { page_path: location.pathname });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, progress?.auto_opened_first_time]);

  // Track visited pages
  useEffect(() => {
    if (!progress) return;
    const path = location.pathname;
    if (progress.visited_pages?.[path]) return;

    const updatedVisited = { ...progress.visited_pages, [path]: new Date().toISOString() };
    update({ visited_pages: updatedVisited }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, progress?.id]);

  // Auto-navigate to the step's route when the active step belongs elsewhere
  useEffect(() => {
    if (!isOpen || !currentStep?.route) return;
    if (currentStep.route === location.pathname) return;
    navigate(currentStep.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentStep?.id, currentStep?.route]);

  const open = useCallback(() => {
    setIsOpen(true);
    if (progress?.tour_status === "not_started") {
      update({ tour_status: "in_progress" }).catch(() => {});
    }
    logEvent(currentStepId, "viewed", { page_path: location.pathname });
  }, [progress?.tour_status, currentStepId, location.pathname, update, logEvent]);

  const close = useCallback(() => {
    setIsOpen(false);
    update({
      dismissed_count: (progress?.dismissed_count ?? 0) + 1,
    }).catch(() => {});
    logEvent(currentStepId, "dismiss", { page_path: location.pathname });
  }, [progress?.dismissed_count, currentStepId, location.pathname, update, logEvent]);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  const goToStep = useCallback(
    (stepId: string, eventType: "next" | "back" | "option_selected" | "auto_advanced", optionId?: string) => {
      const step = getStepById(stepId);
      if (!step) return;

      const isLastStep = stepId === "tour_completed";
      update({
        current_step_id: stepId,
        tour_status: isLastStep ? "completed" : "in_progress",
      }).catch(() => {});

      logEvent(stepId, eventType, {
        selected_option_id: optionId,
        page_path: location.pathname,
      });
    },
    [update, logEvent, location.pathname],
  );

  const next = useCallback(() => {
    // Prefer explicit nextStepId, fall back to linear order in registry
    let target = currentStep?.nextStepId;
    if (!target && currentStep) {
      const idx = tourStepsRegistry.findIndex((s) => s.id === currentStep.id);
      target = tourStepsRegistry[idx + 1]?.id;
    }
    if (!target) return;
    goToStep(target, "next");
  }, [currentStep, goToStep]);

  const back = useCallback(() => {
    let target = currentStep?.previousStepId;
    if (!target && currentStep) {
      const idx = tourStepsRegistry.findIndex((s) => s.id === currentStep.id);
      target = tourStepsRegistry[idx - 1]?.id;
    }
    if (!target) return;
    goToStep(target, "back");
  }, [currentStep, goToStep]);

  const skip = useCallback(() => {
    if (!progress) return;
    const skipped = Array.from(new Set([...(progress.skipped_steps ?? []), currentStepId]));
    update({ skipped_steps: skipped }).catch(() => {});
    logEvent(currentStepId, "skip", { page_path: location.pathname });

    if (currentStep?.nextStepId) goToStep(currentStep.nextStepId, "auto_advanced");
    else setIsOpen(false);
  }, [progress, currentStepId, currentStep, update, logEvent, location.pathname, goToStep]);

  const selectOption = useCallback(
    (option: TourStepOption) => {
      if (option.dismisses) {
        setIsOpen(false);
        update({
          dismissed_count: (progress?.dismissed_count ?? 0) + 1,
        }).catch(() => {});
        logEvent(currentStepId, "dismiss", {
          selected_option_id: option.id,
          page_path: location.pathname,
        });
        return;
      }

      if (option.navigatesTo && option.navigatesTo !== location.pathname) {
        navigate(option.navigatesTo);
      }

      if (option.goesToStepId) {
        goToStep(option.goesToStepId, "option_selected", option.id);
      } else {
        logEvent(currentStepId, "option_selected", {
          selected_option_id: option.id,
          page_path: location.pathname,
        });
      }
    },
    [navigate, location.pathname, progress?.dismissed_count, currentStepId, update, logEvent, goToStep],
  );

  const reset = useCallback(() => {
    update({
      current_step_id: "welcome",
      tour_status: "reset",
      visited_pages: {},
      completed_actions: {},
      skipped_steps: [],
      dismissed_count: 0,
      auto_opened_first_time: false,
    }).then(() => {
      setIsOpen(true);
    }).catch(() => {});
    logEvent("welcome", "reset", { page_path: location.pathname });
  }, [update, logEvent, location.pathname]);

  // ── Preferences ─────────────────────────────────────────────────────────
  const preferences = useMemo<TourPreferences>(() => {
    const raw = (progress?.preferences ?? {}) as Partial<TourPreferences>;
    return {
      language: raw.language ?? "en",
      show_highlights: raw.show_highlights ?? true,
      tooltip_mode: (raw.tooltip_mode as TourTooltipMode) ?? "anchored",
    };
  }, [progress?.preferences]);

  const setPreferences = useCallback(
    (patch: Partial<TourPreferences>) => {
      const merged = { ...preferences, ...patch };
      update({ preferences: merged }).catch(() => {});
    },
    [preferences, update],
  );

  const value = useMemo<TourGuideContextValue>(
    () => ({
      isOpen,
      currentStep,
      isReady: !isLoading && !!progress,
      open,
      close,
      toggle,
      next,
      back,
      skip,
      selectOption,
      reset,
      isCompleted: !!isCompleted,
      progressRatio,
      preferences,
      setPreferences,
    }),
    [isOpen, currentStep, isLoading, progress, open, close, toggle, next, back, skip, selectOption, reset, isCompleted, progressRatio, preferences, setPreferences],
  );

  return <TourGuideContext.Provider value={value}>{children}</TourGuideContext.Provider>;
};
